import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCaptureState } from '@/api/capture';
import { useT } from '@/i18n/runtime';
import { shotText } from '@/i18n/serverText';
import { useCaptureStore } from '@/store/captureStore';
import { tenantFrom, tenantFromSummary } from '@/theme/tenant';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { Icon } from './components/Icon';
import { SecurePill } from './components/SecurePill';
import { CentredNotice, Shell } from './components/Shell';

/**
 * `/done` — the receipt (FSD §2.1 CX-03): reference, receipt, what was captured, what happens
 * next (a PERSON decides), how to raise a concern, and the honest limits of the record — what the
 * hash chain and the device signature do and do not show. Says "Submitted", never a verdict.
 */
/** SV-5-2: how often the receipt re-reads the case while it may still be escalated to a live call. */
export const LIVE_WATCH_INTERVAL_MS = 15_000;

export function Done({ watchLive = true }: { watchLive?: boolean } = {}): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const summary = useCaptureStore((s) => s.summary);
  const receipt = useCaptureStore((s) => s.receipt);
  const signature = useCaptureStore((s) => s.signature);
  const completed = useCaptureStore((s) => s.completed);
  const captureState = useCaptureStore((s) => s.captureState);
  const visit = useCaptureStore((s) => s.visit);
  const geoRecorded = useCaptureStore((s) => s.geoRecorded);
  const tenant = tenantFromSummary(summary) ?? tenantFrom('');
  const setCaptureState = useCaptureStore((s) => s.setCaptureState);
  const [liveJoinable, setLiveJoinable] = useState(captureState?.liveLeg?.joinable === true);

  // SV-5-2: after submit the reviewer may escalate to a live call while this page is still open — re-read the case
  // every LIVE_WATCH_INTERVAL_MS and surface "Join the live verification" when the be says the leg is joinable.
  // Stops on its own once the case is decided or withdrawn (nothing further can turn into a live request).
  useEffect(() => {
    if (!watchLive || !receipt) return undefined;
    let active = true;
    const read = async (): Promise<void> => {
      try {
        const st = await getCaptureState();
        if (!active) return;
        const joinable = st.liveLeg?.joinable === true;
        // write only on a change — a 15 s poll that rewrote identical state would re-render the receipt for nothing
        if (joinable !== (useCaptureStore.getState().captureState?.liveLeg?.joinable === true)) setCaptureState(st);
        setLiveJoinable(joinable);
      } catch {
        /* an unreadable state changes nothing on the receipt */
      }
    };
    void read();
    const timer = setInterval(() => void read(), LIVE_WATCH_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [watchLive, receipt, setCaptureState]);

  if (!receipt || !summary) {
    return <Shell tenant={null}><CentredNotice title={t('sv.entry.linkRequired.title')} body={t('sv.entry.linkRequired.body')} /></Shell>;
  }
  const done = new Set([...(captureState?.completedShots ?? []), ...completed]);
  const captured = (visit?.script ?? []).filter((s) => done.has(s.shotId));

  return (
    <Shell tenant={tenant}>
      {/* WCAG 2.1.1 (axe scrollable-region-focusable): the receipt scrolls and may hold no control, so the region
          itself takes keyboard focus and is named */}
      <div className="app-view" tabIndex={0} role="region" aria-label={t('sv.done.title')}>
        <AppBar tenant={tenant} subtitle={t('sv.subtitle')} right={<SecurePill kind="submitted" label={t('sv.pill.submitted')} />} />
        <div className="stack">
          <div className="center-col" style={{ marginTop: 8 }}>
            <span className="success-mark"><Icon name="check" size={28} /></span>
            <div className="h-title sm" data-testid="done-title">{t('sv.done.title')}</div>
            <p className="sub" style={{ marginTop: 6 }}>{t('sv.done.sub')}</p>
          </div>

          {liveJoinable ? (
            <div className="card pad" data-testid="receipt-live-card">
              <div className="pt">{t('sv.live.card.title')}</div>
              <p className="pd" style={{ marginTop: 4 }}>{t('sv.live.card.body')}</p>
              <Button onClick={() => navigate('/live')} data-testid="receipt-join-live" style={{ marginTop: 12 }}>
                {t('sv.live.card.action')}
              </Button>
            </div>
          ) : null}

          <div className="card notice-summary" data-testid="receipt">
            <div className="purpose">
              <span className="pic"><Icon name="doc" size={16} /></span>
              <div>
                <div className="pt">{t('sv.done.reference')}</div>
                <div className="pd mono" data-testid="receipt-reference">{summary.clientRef}</div>
              </div>
            </div>
            <div className="purpose">
              <span className="pic"><Icon name="shield-check" size={16} /></span>
              <div>
                <div className="pt">{t('sv.done.receipt')}</div>
                <div className="pd mono" data-testid="receipt-ref">{receipt.receiptRef}</div>
              </div>
            </div>
          </div>

          <div className="card pad">
            <div className="pt" style={{ marginBottom: 6 }}>{t('sv.done.captured')}</div>
            <ul className="sv-capture-list" data-testid="receipt-captured">
              {captured.map((s) => (
                <li key={s.shotId} dir="auto">{shotText(s, locale).text}</li>
              ))}
            </ul>
          </div>

          <div className="card pad">
            <div className="pt">{t('sv.done.next')}</div>
            <p className="pd" style={{ marginTop: 4 }}>{t('sv.done.next.body', { tenantName: summary.tenantName })}</p>
          </div>

          <div className="card pad" data-testid="receipt-concern">
            <div className="pt">{t('sv.done.concern.title')}</div>
            <p className="pd" style={{ marginTop: 4 }}>{t('sv.done.concern.body', { tenantName: summary.tenantName })}</p>
          </div>

          <div className="card pad sv-limits" data-testid="receipt-limits">
            <div className="pt">{t('sv.done.limits.title')}</div>
            <p className="pd" style={{ marginTop: 4 }}>{t('sv.done.limits.chain')}</p>
            <p className="pd">{signature.state === 'available' ? t('sv.done.limits.signed') : t('sv.done.limits.unsigned')}</p>
            {!geoRecorded ? <p className="pd">{t('sv.done.limits.geo.missing')}</p> : null}
          </div>

          <p className="sub-sm" style={{ textAlign: 'center' }}>{t('sv.done.close')}</p>
        </div>
      </div>
    </Shell>
  );
}
